import {
  DialogFeedbackContext,
  DialogFeedbackIntegration,
  GridFeedbackContext,
  GridFeedbackIntegration
} from "@appretio/ui-kit";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { DialogFeedback } from "./components/dialogFeedback/DialogFeedback";
import {
  createDiagnosticsRecorder,
  DiagnosticsRecorder,
  snapshotDiagnostics
} from "./diagnostics";
import {
  FeedbackApi,
  FeedbackApiContext,
  FeedbackOpenDialog,
  FeedbackPageContextGetter,
  FeedbackPrefill,
  FeedbackState,
  FeedbackStateContext
} from "./FeedbackContext";
import { buildGridFeedbackMenuItems } from "./gridCellReport";

/** A value two getters can co-write one level deep (not arrays/classes). */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    (Object.getPrototypeOf(value) === Object.prototype ||
      Object.getPrototypeOf(value) === null)
  );
}

/**
 * Merge one getter's keys into the collected context. Plain-object values
 * merge one level deep — the shared context selector contributes
 * `top_bar.selector_draft` while the page contributes `top_bar.toggles` —
 * everything else is last-writer-wins, same as before.
 */
function mergeContextBlocks(
  collected: Record<string, unknown>,
  next: Record<string, unknown>
): void {
  for (const [key, value] of Object.entries(next)) {
    const existing = collected[key];
    collected[key] =
      isPlainObject(existing) && isPlainObject(value)
        ? { ...existing, ...value }
        : value;
  }
}

/** One dialog's own capture block — a broken getter must not cost the report. */
function readDialogContext(
  dialog: FeedbackOpenDialog
): Record<string, unknown> {
  try {
    return dialog.getContext?.() ?? {};
  } catch {
    return {};
  }
}

/**
 * The `modal` block: the topmost open dialog, plus the names of the
 * reporting dialogs under it — a `hideFeedbackIcon` dialog registers
 * nothing, so confirmations never appear in `stack`. The descriptor's own
 * keys are written last so a dialog's getter cannot shadow them.
 */
function describeOpenDialogs(
  dialogs: FeedbackOpenDialog[]
): Record<string, unknown> | null {
  const topmost = dialogs[dialogs.length - 1];
  if (!topmost) return null;
  return {
    ...readDialogContext(topmost),
    // Assigned, not conditionally spread: an undefined `name` still has to
    // overwrite whatever the getter put there, or the guarantee above only
    // holds for dialogs that happen to set every field. JSON.stringify drops
    // the undefined ones, so the wire format is unchanged.
    name: topmost.name,
    title: topmost.title,
    size: topmost.size,
    opened_at: topmost.opened_at,
    stack: dialogs.map((dialog) => dialog.name ?? dialog.title ?? "unnamed")
  };
}

/**
 * Holds the feedback modal's open state (plus any prefill) so any entry
 * point — the header button, a grid context-menu item — can open it. Also
 * keeps the registry of page-context getters pages contribute to reports,
 * and provides ui-kit's GridFeedbackContext so every grid under this
 * provider grows "Report this value" in its context menu. Wrap the app
 * shell with this, then render <FeedbackModal /> inside it.
 *
 * State and API live in SEPARATE contexts: the modal-open state changes on
 * every open/close, while the API functions keep their identity for the
 * provider's lifetime — so heavy pages that only register context getters
 * (via useRegisterFeedbackContext) never re-render, and their registration
 * effects never re-run, when the modal toggles.
 */
export function FeedbackProvider({ children }: PropsWithChildren) {
  const [isOpen, setIsOpen] = useState(false);
  const [prefill, setPrefill] = useState<FeedbackPrefill | null>(null);
  // A ref, not state: getters register/unregister on page mount and are only
  // read at submit time — re-rendering the provider tree for them is waste.
  // Stable identity also keeps `open`/`registerPageContext` safe to close
  // over in AG Grid callbacks that are frozen at grid init.
  const pageContextGetters = useRef(new Set<FeedbackPageContextGetter>());
  // A Set, not a stack array: dialogs do not always unmount in the order
  // they opened (a host modal can close while a nested confirm is still up),
  // so entries are removed by identity. Insertion order is mount order, so
  // the last entry is the topmost dialog.
  const openDialogs = useRef(new Set<FeedbackOpenDialog>());

  // Diagnostics recorder: console/error hooks live for the provider's
  // lifetime (install is idempotent, so a StrictMode double-mount can't
  // double-wrap; unmount restores the originals). Its snapshot rides every
  // report as `context.diagnostics`. Lazily initialized — `useRef(create())`
  // would construct (and discard) a fresh recorder on every render.
  const diagnostics = useRef<DiagnosticsRecorder | null>(null);
  diagnostics.current ??= createDiagnosticsRecorder();
  useEffect(() => diagnostics.current?.install(), []);

  const open = useCallback((nextPrefill?: FeedbackPrefill) => {
    setPrefill(nextPrefill ?? null);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setPrefill(null);
  }, []);

  const registerPageContext = useCallback(
    (getter: FeedbackPageContextGetter) => {
      pageContextGetters.current.add(getter);
      return () => {
        pageContextGetters.current.delete(getter);
      };
    },
    []
  );

  const registerOpenDialog = useCallback((dialog: FeedbackOpenDialog) => {
    openDialogs.current.add(dialog);
    return () => {
      openDialogs.current.delete(dialog);
    };
  }, []);

  const collectPageContext = useCallback(() => {
    const collected: Record<string, unknown> = {};
    for (const getter of pageContextGetters.current) {
      try {
        mergeContextBlocks(collected, getter());
      } catch {
        // A broken page getter must never block sending feedback.
      }
    }
    // Assigned after the loop, like `diagnostics` below: the open dialogs
    // are the single owner of `modal`, so no page getter can co-write it.
    const modal = describeOpenDialogs([...openDialogs.current]);
    if (modal) {
      collected.modal = modal;
    }
    try {
      if (diagnostics.current) {
        collected.diagnostics = snapshotDiagnostics(diagnostics.current);
      }
    } catch {
      // Diagnostics are additive — never block a report on them.
    }
    return collected;
  }, []);

  // Every dep is a stable useCallback, so this value never changes — the
  // whole point of the API/state context split.
  const api = useMemo<FeedbackApi>(
    () => ({
      open,
      close,
      registerPageContext,
      registerOpenDialog,
      collectPageContext
    }),
    [open, close, registerPageContext, registerOpenDialog, collectPageContext]
  );

  const state = useMemo<FeedbackState>(
    () => ({ isOpen, prefill }),
    [isOpen, prefill]
  );

  // The grid-side bridge: ui-kit's DataTable (and the domain grid wrappers)
  // consume this to append the report item — ui-kit defines the context so
  // it never depends on this package; this package supplies the behavior.
  const gridFeedback = useMemo<GridFeedbackIntegration>(
    () => ({
      buildMenuItems: (params, describeRow) =>
        buildGridFeedbackMenuItems(params, open, describeRow)
    }),
    [open]
  );

  // The dialog-side bridge, same inversion as the grid one. Memoized: an
  // unstable value remounts DialogFeedback on every provider render, which
  // would re-register every open dialog in a loop.
  const dialogFeedback = useMemo<DialogFeedbackIntegration>(
    () => ({ DialogFeedback }),
    []
  );

  return (
    <FeedbackApiContext.Provider value={api}>
      <FeedbackStateContext.Provider value={state}>
        <GridFeedbackContext.Provider value={gridFeedback}>
          <DialogFeedbackContext.Provider value={dialogFeedback}>
            {children}
          </DialogFeedbackContext.Provider>
        </GridFeedbackContext.Provider>
      </FeedbackStateContext.Provider>
    </FeedbackApiContext.Provider>
  );
}
